import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, ChevronsDown, ChevronsUp, Download } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Select,
  Table,
  Td,
  Th,
} from '../components/ui';
import { DataTable, type Column } from '../components/data-table';
import { DualList } from '../components/DualList';
import { PatternRows, type DialPatternRow } from '../components/PatternRows';
import { FormFooter, Grid, PageHeader, Row, Section, Toggle } from '../components/form';
import { DestinationPicker, type DestinationValue } from '../components/DestinationPicker';
import { api, type Trunk } from '../lib/api';

interface InboundRoute {
  id: string;
  name: string;
  // Present on the detail response, which is what the edit form reads.
  destinationId?: string | null;
  destinationValue?: string | null;
  didPattern: string;
  priority: number;
  destinationType: string;
  isActive: boolean;
}

interface OutboundRoute {
  callerIdOverride?: string | null;
  id: string;
  name: string;
  pattern: string;
  priority: number;
  strip: number;
  prepend: string | null;
  isActive: boolean;
}

/**
 * Kept as a redirect target for anybody with the old URL bookmarked.
 *
 * Inbound and outbound are separate screens now: they are edited by different people at different
 * times, and stacking them meant scrolling past a table you were not working on to reach the one
 * you were.
 */
export function Routing() {
  return <OutboundRoutes />;
}

/**
 * Download the current list as CSV.
 *
 * Client-side because the rows are already here — a round trip to render a file from data the
 * browser is holding is latency for nothing. Every field is quoted and embedded quotes doubled: a
 * route name can legitimately contain a comma, and one unquoted field shifts every column after
 * it for that row alone, which is the kind of corruption somebody notices three imports later.
 */
function exportCsv<T extends object>(
  filename: string,
  rows: readonly T[],
  fields: ReadonlyArray<keyof T & string>,
): void {
  const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const csv = [
    fields.map(escape).join(','),
    ...rows.map((row) => fields.map((field) => escape(row[field])).join(',')),
  ].join('\r\n');

  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  link.click();
  // Revoked, or every export leaks the whole file until the tab is closed.
  URL.revokeObjectURL(url);
}

/**
 * Move a route in the matching order.
 *
 * Buttons rather than a priority field: priority decides which route wins when two patterns both
 * match, and asking somebody to type numbers that must stay distinct and ordered is how a list
 * ends up with six routes all at 100.
 */
function MoveButtons({
  onMove,
  disabled,
}: {
  onMove: (to: 'top' | 'up' | 'down' | 'bottom') => void;
  disabled: boolean;
}) {
  const actions = [
    { to: 'top', Icon: ChevronsUp, label: 'Move to top' },
    { to: 'up', Icon: ChevronUp, label: 'Move up' },
    { to: 'down', Icon: ChevronDown, label: 'Move down' },
    { to: 'bottom', Icon: ChevronsDown, label: 'Move to bottom' },
  ] as const;

  return (
    <span className="flex">
      {actions.map(({ to, Icon, label }) => (
        <Button
          key={to}
          variant="ghost"
          className="h-8 px-1.5"
          title={label}
          aria-label={label}
          disabled={disabled}
          onClick={() => onMove(to)}
        >
          <Icon className="size-4" />
        </Button>
      ))}
    </span>
  );
}

// -------------------------------------------------------------------------------------------

export function OutboundRoutes() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);

  const { data: routes = [] } = useQuery({
    queryKey: ['outbound-routes'],
    queryFn: () => api.get<OutboundRoute[]>('/api/v1/outbound-routes'),
  });

  const remove = useMutation({
    mutationFn: async (targets: readonly OutboundRoute[]) => {
      for (const r of targets) await api.del(`/api/v1/outbound-routes/${r.id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['outbound-routes'] }),
  });

  const move = useMutation({
    mutationFn: ({ id, to }: { id: string; to: 'top' | 'up' | 'down' | 'bottom' }) =>
      api.post(`/api/v1/outbound-routes/${id}/move`, { to }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['outbound-routes'] }),
  });

  const columns: ReadonlyArray<Column<OutboundRoute>> = [
    {
      key: 'priority',
      header: 'Priority',
      width: '6rem',
      sort: (a, b) => a.priority - b.priority,
      render: (r) => <span className="font-mono tabular-nums">{r.priority}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (r) => r.name,
    },
    { key: 'pattern', header: 'Pattern', render: (r) => <code>{r.pattern}</code> },
    {
      key: 'digits',
      header: 'Digit changes',
      render: (r) => (
        <span className="text-muted-foreground">
          {r.strip === 0 && !r.prepend
            ? '—'
            : [r.strip > 0 ? `strip ${r.strip}` : null, r.prepend ? `prepend ${r.prepend}` : null]
                .filter(Boolean)
                .join(', ')}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (r.isActive ? <Badge tone="success">Active</Badge> : <Badge>Disabled</Badge>),
    },
    {
      key: 'move',
      header: 'Move',
      width: '10rem',
      render: (r) => (
        <MoveButtons disabled={move.isPending} onMove={(to) => move.mutate({ id: r.id, to })} />
      ),
    },
  ];

  if (editing !== null) {
    return (
      <CreateOutbound
        id={editing === 'new' ? null : editing}
        onCancel={() => setEditing(null)}
        onCreated={() => {
          setEditing(null);
          void queryClient.invalidateQueries({ queryKey: ['outbound-routes'] });
        }}
      />
    );
  }

  return (
    <section className="space-y-4">
      <header>
        <h1 className="text-lg font-semibold">Outbound routing</h1>
        <p className="text-sm text-muted-foreground">
          Which trunk carries a call, based on the number dialled. Lowest priority wins.
        </p>
      </header>

      <Simulator />

      <DataTable
        rows={routes}
        columns={columns}
        rowKey={(r) => r.id}
        search={(r) => `${r.name} ${r.pattern}`}
        searchPlaceholder="Search routes"
        toolbar={
          <span className="flex gap-2">
            <Button onClick={() => setEditing('new')}>New route</Button>
            <Button
              variant="secondary"
              onClick={() =>
                exportCsv('outbound-routes', routes, [
                  'name',
                  'pattern',
                  'strip',
                  'prepend',
                  'priority',
                  'isActive',
                ])
              }
            >
              <Download className="mr-1.5 size-4" />
              Export
            </Button>
          </span>
        }
        onEdit={(r) => setEditing(r.id)}
        onDelete={(targets) => remove.mutate(targets)}
        deleteLabel={(targets) =>
          targets.length === 1
            ? `Delete "${targets[0]!.name}"? Calls matching ${targets[0]!.pattern} will stop going out.`
            : `Delete ${targets.length} outbound routes? Calls matching them will stop going out.`
        }
        empty="No outbound routes. Without one, nobody can call out."
      />
    </section>
  );
}

/**
 * "If I dial this, what happens?"
 *
 * The most valuable control on this page. Outbound routing mistakes are invisible until the
 * carrier bill arrives, so being able to check the resolved route, digit manipulation and trunk
 * failover order before saving is cheap insurance.
 */
function Simulator() {
  const [number, setNumber] = useState('');
  // Who is dialling decides which routes are even considered, so the tester has to ask.
  const [extensionId, setExtensionId] = useState('');

  const { data: extensions = [] } = useQuery({
    queryKey: ['extensions'],
    queryFn: () =>
      api.get<Array<{ id: string; number: string; name: string }>>('/api/v1/extensions'),
  });

  const simulate = useMutation({
    mutationFn: (value: string) =>
      api.post<{
        matched: boolean;
        route: { name: string; pattern: string } | null;
        strip: number;
        prepend: string | null;
        dialString: string | null;
        trunkOrder: Array<{ id: string; name: string }>;
        reason?: string;
      }>('/api/v1/outbound-routes/simulate', {
        number: value,
        ...(extensionId ? { extensionId } : {}),
      }),
  });

  const result = simulate.data;

  return (
    <Card className="space-y-3">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (number) simulate.mutate(number);
        }}
      >
        <div className="min-w-48 flex-1">
          <Field label="Test a number">
            <Input
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="912125551234"
            />
          </Field>
        </div>
        <div className="min-w-48">
          <Field label="Dialled by">
            <Select value={extensionId} onChange={setExtensionId}>
              <option value="">Anybody (unrestricted routes only)</option>
              {extensions.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.number} — {e.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Button type="submit" variant="secondary" disabled={!number || simulate.isPending}>
          Simulate
        </Button>
      </form>

      {result ? (
        result.matched ? (
          <div className="space-y-1 rounded-[--radius] bg-muted p-3 text-sm">
            <p>
              Route <strong>{result.route?.name}</strong>{' '}
              <span className="font-mono text-muted-foreground">({result.route?.pattern})</span>
            </p>
            <p>
              Dials <span className="font-mono">{result.dialString}</span>
              {result.strip > 0 || result.prepend ? (
                <span className="text-muted-foreground">
                  {' '}
                  — strip {result.strip}
                  {result.prepend ? `, prepend ${result.prepend}` : ''}
                </span>
              ) : null}
            </p>
            <p className="text-muted-foreground">
              Trunks tried in order: {result.trunkOrder.map((t) => t.name).join(' → ') || 'none'}
            </p>
            {result.reason ? <Alert tone="warning">{result.reason}</Alert> : null}
          </div>
        ) : (
          <Alert tone="warning">{result.reason ?? 'No route matches this number.'}</Alert>
        )
      ) : null}
    </Card>
  );
}

function CreateOutbound({
  id,
  onCancel,
  onCreated,
}: {
  id: string | null;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const creating = id === null;
  const [form, setForm] = useState({ name: '', priority: 100, callerIdOverride: '' });
  const [patterns, setPatterns] = useState<DialPatternRow[]>([
    { pattern: 'X.', strip: 0, prepend: '' },
  ]);
  const [trunkIds, setTrunkIds] = useState<string[]>([]);
  const [extensionIds, setExtensionIds] = useState<string[]>([]);
  const [extensionGroupIds, setExtensionGroupIds] = useState<string[]>([]);
  const [pinListId, setPinListId] = useState('');
  const [rrmemoryHunt, setRrmemoryHunt] = useState(false);

  const { data: trunks = [] } = useQuery({
    queryKey: ['trunks'],
    queryFn: () => api.get<Trunk[]>('/api/v1/trunks'),
  });

  const { data: extensions = [] } = useQuery({
    queryKey: ['extensions'],
    queryFn: () =>
      api.get<Array<{ id: string; number: string; name: string }>>('/api/v1/extensions'),
  });

  const { data: extensionGroups = [] } = useQuery({
    queryKey: ['extension-groups'],
    queryFn: () =>
      api.get<Array<{ id: string; name: string; members: unknown[] }>>('/api/v1/extension-groups'),
  });

  const { data: pinLists = [] } = useQuery({
    queryKey: ['pin-lists'],
    queryFn: () => api.get<Array<{ id: string; name: string }>>('/api/v1/pin-lists'),
  });

  const { data: existing } = useQuery({
    queryKey: ['outbound-route', id],
    queryFn: () =>
      api.get<
        OutboundRoute & {
          patterns: DialPatternRow[];
          trunkIds: string[];
          extensionIds: string[];
          extensionGroupIds: string[];
          pinListId: string | null;
          rrmemoryHunt: boolean;
        }
      >(`/api/v1/outbound-routes/${id}`),
    enabled: !creating,
  });

  useEffect(() => {
    if (!existing) return;
    setForm({
      name: existing.name,
      priority: existing.priority,
      callerIdOverride: existing.callerIdOverride ?? '',
    });
    setPatterns(
      existing.patterns.length > 0 ? existing.patterns : [{ pattern: 'X.', strip: 0, prepend: '' }],
    );
    // Kept in the order the server returned, which is the failover order.
    setTrunkIds(existing.trunkIds ?? []);
    setExtensionIds(existing.extensionIds ?? []);
    setExtensionGroupIds(existing.extensionGroupIds ?? []);
    setPinListId(existing.pinListId ?? '');
    setRrmemoryHunt(existing.rrmemoryHunt ?? false);
  }, [existing]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        priority: form.priority,
        patterns: patterns
          .filter((p) => p.pattern.trim())
          .map((p) => ({
            pattern: p.pattern.trim(),
            strip: p.strip,
            ...(p.prepend ? { prepend: p.prepend } : {}),
          })),
        trunkIds,
        extensionIds,
        extensionGroupIds,
        ...(form.callerIdOverride ? { callerIdOverride: form.callerIdOverride } : {}),
        pinListId: pinListId || null,
        rrmemoryHunt,
      };
      return creating
        ? api.post('/api/v1/outbound-routes', body)
        : api.put(`/api/v1/outbound-routes/${id}`, body);
    },
    onSuccess: onCreated,
  });

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <PageHeader
        title={creating ? 'Add outbound route' : `Edit ${form.name || 'outbound route'}`}
        description="Which trunk carries a call, and which digits are sent."
      />

      <Section title="General">
        <Grid>
          <Row label="Name" required>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="UK national"
              required
            />
          </Row>
          <Row
            label="Outbound caller ID"
            hint="What the far end sees. Most carriers only allow numbers you own and substitute their own otherwise."
          >
            <Input
              value={form.callerIdOverride}
              onChange={(e) => setForm({ ...form, callerIdOverride: e.target.value })}
              placeholder="+442071234567"
            />
          </Row>
        </Grid>
      </Section>

      <Section
        title="Dial patterns"
        description="Asterisk syntax: X any digit, N 2-9, Z 1-9, . one or more. Strip removes leading digits; prepend adds them back."
      >
        <PatternRows rows={patterns} onChange={setPatterns} />
      </Section>

      <Section
        title="Trunks"
        description="Tried in order. The first that answers carries the call; the rest are failover."
      >
        <DualList
          options={trunks.map((t) => ({ id: t.id, label: t.name, detail: t.mode }))}
          selected={trunkIds}
          onChange={setTrunkIds}
          detailHeader="Trunk type"
          ordered
          emptyHint="No trunks selected — this route cannot carry a call."
        />

        <Grid>
          <Row
            label="Outbound route password"
            hint="Callers quote a PIN before the route will carry the call."
          >
            <Select value={pinListId} onChange={setPinListId}>
              <option value="">Disabled</option>
              {pinLists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </Select>
          </Row>
          <Row
            label="Round-robin hunt"
            hint="Start from the trunk after the last one used, rather than always the first."
          >
            <div className="flex h-9 items-center gap-3">
              <Toggle checked={rrmemoryHunt} onChange={setRrmemoryHunt} label="Round-robin hunt" />
              <span className="text-sm text-muted-foreground">{rrmemoryHunt ? 'On' : 'Off'}</span>
            </div>
          </Row>
        </Grid>
      </Section>

      <Section
        title="Extensions and extension groups"
        description="Who may use this route. Leave both empty to allow everybody."
      >
        <DualList
          options={extensions.map((e) => ({ id: e.id, label: e.number, detail: e.name }))}
          selected={extensionIds}
          onChange={setExtensionIds}
          labelHeader="Extension"
          detailHeader="Name"
          emptyHint="Everybody may use this route."
        />

        <p className="pt-2 text-xs font-medium text-muted-foreground">Extension groups</p>
        {/*
          Groups stay groups rather than being flattened into the list above on save: a group is
          picked precisely so that somebody joining it later gains the route.
        */}
        <DualList
          options={extensionGroups.map((g) => ({
            id: g.id,
            label: g.name,
            detail: `${g.members.length} members`,
          }))}
          selected={extensionGroupIds}
          onChange={setExtensionGroupIds}
          labelHeader="Group"
          detailHeader="Members"
          emptyHint="No groups selected."
        />
      </Section>

      {save.error ? <Alert>{(save.error as Error).message}</Alert> : null}

      <FormFooter
        onCancel={onCancel}
        saving={save.isPending}
        saveLabel={creating ? 'Create' : 'Save'}
        disabled={!form.name || trunkIds.length === 0 || patterns.every((p) => !p.pattern.trim())}
      />
    </form>
  );
}

export function InboundRoutes() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);

  const { data: routes = [] } = useQuery({
    queryKey: ['inbound-routes'],
    queryFn: () => api.get<InboundRoute[]>('/api/v1/inbound-routes'),
  });

  const remove = useMutation({
    mutationFn: async (targets: readonly InboundRoute[]) => {
      for (const r of targets) await api.del(`/api/v1/inbound-routes/${r.id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inbound-routes'] }),
  });

  const move = useMutation({
    mutationFn: ({ id, to }: { id: string; to: 'top' | 'up' | 'down' | 'bottom' }) =>
      api.post(`/api/v1/inbound-routes/${id}/move`, { to }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inbound-routes'] }),
  });

  const columns: ReadonlyArray<Column<InboundRoute>> = [
    {
      key: 'priority',
      header: 'Priority',
      width: '6rem',
      sort: (a, b) => a.priority - b.priority,
      render: (r) => <span className="font-mono tabular-nums">{r.priority}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (r) => r.name,
    },
    { key: 'did', header: 'Number pattern', render: (r) => <code>{r.didPattern}</code> },
    {
      key: 'destination',
      header: 'Goes to',
      render: (r) => r.destinationType.replace('_', ' '),
    },
    {
      key: 'move',
      header: 'Move',
      width: '10rem',
      render: (r) => (
        <MoveButtons disabled={move.isPending} onMove={(to) => move.mutate({ id: r.id, to })} />
      ),
    },
  ];

  if (editing !== null) {
    return (
      <CreateInbound
        id={editing === 'new' ? null : editing}
        onCancel={() => setEditing(null)}
        onCreated={() => {
          setEditing(null);
          void queryClient.invalidateQueries({ queryKey: ['inbound-routes'] });
        }}
      />
    );
  }

  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold">Inbound routing</h2>
        <p className="text-sm text-muted-foreground">
          Where a call goes when it arrives on one of your numbers.
        </p>
      </header>

      <DataTable
        rows={routes}
        columns={columns}
        rowKey={(r) => r.id}
        search={(r) => `${r.name} ${r.didPattern}`}
        searchPlaceholder="Search routes"
        toolbar={
          <span className="flex gap-2">
            <Button onClick={() => setEditing('new')}>New route</Button>
            <Button
              variant="secondary"
              onClick={() =>
                exportCsv('inbound-routes', routes, [
                  'name',
                  'didPattern',
                  'destinationType',
                  'priority',
                  'isActive',
                ])
              }
            >
              <Download className="mr-1.5 size-4" />
              Export
            </Button>
          </span>
        }
        onEdit={(r) => setEditing(r.id)}
        onDelete={(targets) => remove.mutate(targets)}
        deleteLabel={(targets) =>
          targets.length === 1
            ? `Delete "${targets[0]!.name}"? Calls to ${targets[0]!.didPattern} will have nowhere to go.`
            : `Delete ${targets.length} inbound routes? Calls matching them will have nowhere to go.`
        }
        empty="No inbound routes. Incoming calls have nowhere to go until you add one."
      />
    </section>
  );
}

function CreateInbound({
  id,
  onCancel,
  onCreated,
}: {
  id: string | null;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const creating = id === null;
  const [form, setForm] = useState({ name: '', priority: 100, alertInfo: '' });
  const [didPatterns, setDidPatterns] = useState<DialPatternRow[]>([
    { pattern: '_.', strip: 0, prepend: '' },
  ]);
  const [cidPatterns, setCidPatterns] = useState<DialPatternRow[]>([]);
  const [trunkIds, setTrunkIds] = useState<string[]>([]);
  const [faxDetection, setFaxDetection] = useState(false);
  const [destination, setDestination] = useState<DestinationValue>({
    destinationType: 'hangup',
    destinationId: null,
    destinationValue: null,
  });
  const [faxDestination, setFaxDestination] = useState<DestinationValue>({
    destinationType: 'hangup',
    destinationId: null,
    destinationValue: null,
  });

  const { data: trunks = [] } = useQuery({
    queryKey: ['trunks'],
    queryFn: () => api.get<Trunk[]>('/api/v1/trunks'),
  });

  const { data: existing } = useQuery({
    queryKey: ['inbound-route', id],
    queryFn: () =>
      api.get<
        InboundRoute & {
          didPatterns: string[];
          cidPatterns: string[];
          trunkIds: string[];
          alertInfo: string | null;
          faxDetection: boolean;
          faxDestinationType: string | null;
          faxDestinationId: string | null;
          faxDestinationValue: string | null;
        }
      >(`/api/v1/inbound-routes/${id}`),
    enabled: !creating,
  });

  useEffect(() => {
    if (!existing) return;
    setForm({
      name: existing.name,
      priority: existing.priority,
      alertInfo: existing.alertInfo ?? '',
    });
    const rows = (values: string[]) =>
      values.map((pattern) => ({ pattern, strip: 0, prepend: '' }));
    setDidPatterns(
      existing.didPatterns.length > 0
        ? rows(existing.didPatterns)
        : [{ pattern: '_.', strip: 0, prepend: '' }],
    );
    setCidPatterns(rows(existing.cidPatterns ?? []));
    setTrunkIds(existing.trunkIds ?? []);
    setFaxDetection(existing.faxDetection ?? false);
    setDestination({
      destinationType: existing.destinationType,
      destinationId: existing.destinationId ?? null,
      destinationValue: existing.destinationValue ?? null,
    });
    setFaxDestination({
      destinationType: existing.faxDestinationType ?? 'hangup',
      destinationId: existing.faxDestinationId ?? null,
      destinationValue: existing.faxDestinationValue ?? null,
    });
  }, [existing]);

  const patternValues = (rows: DialPatternRow[]) =>
    rows.map((r) => r.pattern.trim()).filter(Boolean);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        priority: form.priority,
        didPatterns: patternValues(didPatterns),
        cidPatterns: patternValues(cidPatterns),
        trunkIds,
        ...(form.alertInfo ? { alertInfo: form.alertInfo } : {}),
        faxDetection,
        destinationType: destination.destinationType,
        ...(destination.destinationId ? { destinationId: destination.destinationId } : {}),
        ...(destination.destinationValue ? { destinationValue: destination.destinationValue } : {}),
        // Only meaningful when detection is on; sending it otherwise would persist a destination
        // the admin cannot see on the form, which then surprises them when they enable detection.
        ...(faxDetection
          ? {
              faxDestinationType: faxDestination.destinationType,
              ...(faxDestination.destinationId
                ? { faxDestinationId: faxDestination.destinationId }
                : {}),
              ...(faxDestination.destinationValue
                ? { faxDestinationValue: faxDestination.destinationValue }
                : {}),
            }
          : {}),
      };
      return creating
        ? api.post('/api/v1/inbound-routes', body)
        : api.put(`/api/v1/inbound-routes/${id}`, body);
    },
    onSuccess: onCreated,
  });

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <PageHeader
        title={creating ? 'Add inbound route' : `Edit ${form.name || 'inbound route'}`}
        description="Which incoming calls this route claims, and where they go."
      />

      <Section title="General">
        <Grid>
          <Row label="Name" required>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Main number"
              required
            />
          </Row>
          <Row
            label="Distinctive ringtone"
            hint="Sent as Alert-Info. Desk phones that support it ring differently for this route."
          >
            <Input
              value={form.alertInfo}
              onChange={(e) => setForm({ ...form, alertInfo: e.target.value })}
              placeholder="Bellcore-dr2"
            />
          </Row>
        </Grid>
      </Section>

      <Section
        title="DID pattern"
        description="The number dialled. Leave _. to claim every call arriving on the chosen trunks."
      >
        <PatternRows
          rows={didPatterns}
          onChange={setDidPatterns}
          withDigits={false}
          placeholder="_1XXXXXXXXXX"
        />
      </Section>

      <Section
        title="Caller ID pattern"
        description="Optional. Add patterns to claim this route only for particular callers — leave empty for any caller."
      >
        <PatternRows
          rows={cidPatterns}
          onChange={setCidPatterns}
          withDigits={false}
          placeholder="_44XXXXXXXXX"
        />
      </Section>

      <Section
        title="Trunks"
        description="Which trunks this route watches. A call is claimed only if it arrived on one of them."
      >
        <DualList
          options={trunks.map((t) => ({ id: t.id, label: t.name, detail: t.mode }))}
          selected={trunkIds}
          onChange={setTrunkIds}
          detailHeader="Trunk type"
          emptyHint="No trunks selected — this route claims calls from any trunk."
        />
      </Section>

      <Section title="Destination" description="Where a claimed call goes.">
        <DestinationPicker value={destination} onChange={setDestination} />
      </Section>

      <Section
        title="Fax detection"
        description="Listen for the CNG tone in the first few seconds and divert if it is heard."
      >
        <div className="flex items-center gap-3">
          <Toggle checked={faxDetection} onChange={setFaxDetection} label="Detect faxes" />
          <span className="text-sm text-muted-foreground">
            {faxDetection ? 'On' : 'Off — a fax call rings the destination as a voice call.'}
          </span>
        </div>

        {faxDetection && (
          <div className="mt-4">
            <DestinationPicker value={faxDestination} onChange={setFaxDestination} />
          </div>
        )}
      </Section>

      {save.error ? <Alert>{(save.error as Error).message}</Alert> : null}

      <FormFooter
        onCancel={onCancel}
        saving={save.isPending}
        saveLabel={creating ? 'Create' : 'Save'}
        disabled={!form.name || patternValues(didPatterns).length === 0}
      />
    </form>
  );
}
